import axios from "axios";

const NAMABLE_API = process.env.REACT_APP_NAMABLE_API;
const LOCAL_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = "namable_token";
const GUEST_ID_KEY = "namable_guest_id";
const DEVICE_KEY = "namable_device_id";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);
export const getGuestId = () => localStorage.getItem(GUEST_ID_KEY);

function deviceId() {
  let d = localStorage.getItem(DEVICE_KEY);
  if (!d) {
    const rand =
      (crypto?.randomUUID && crypto.randomUUID().replace(/-/g, "")) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    d = "web_" + rand;
    localStorage.setItem(DEVICE_KEY, d);
  }
  return d;
}

const api = axios.create({ baseURL: NAMABLE_API, timeout: 190000 });
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const local = axios.create({ baseURL: LOCAL_API, timeout: 30000 });

// ---------- session ----------
export async function ensureGuestSession() {
  if (getToken()) return null;
  const { data } = await api.post("/guest/session", { device_id: deviceId(), platform: "web" });
  if (data?.token) setToken(data.token);
  if (data?.guest_id) localStorage.setItem(GUEST_ID_KEY, data.guest_id);
  return data;
}

// ---------- auth ----------
export async function apiMe() {
  const { data } = await api.get("/auth/me");
  return data;
}
export async function apiRegister(payload) {
  const { data } = await api.post("/auth/register", { ...payload, guest_id: getGuestId() });
  if (data?.token) setToken(data.token);
  return data;
}
export async function apiLogin(payload) {
  const { data } = await api.post("/auth/login", { ...payload, guest_id: getGuestId() });
  if (data?.token) setToken(data.token);
  return data;
}
export async function apiGoogleSession(session_id) {
  const { data } = await api.post("/auth/google-session", { session_id, guest_id: getGuestId() });
  if (data?.token) setToken(data.token);
  return data;
}
export async function apiLogout() {
  try {
    await api.post("/auth/logout");
  } catch (e) {
    /* ignore */
  }
  clearToken();
  localStorage.removeItem(GUEST_ID_KEY);
}
export async function apiUpdateProfile(payload) {
  const { data } = await api.patch("/auth/profile", payload);
  return data;
}
export async function apiDeleteAccount() {
  const { data } = await api.delete("/auth/account");
  clearToken();
  return data;
}

// ---------- product ----------
export async function apiUsage() {
  const { data } = await api.get("/usage");
  return data;
}
export async function apiExtensions() {
  const { data } = await api.get("/extensions");
  return data;
}
export async function apiCountries() {
  const { data } = await api.get("/countries");
  return data;
}
export async function apiGenerate(payload) {
  const { data } = await api.post("/generate", payload);
  return data;
}
export async function apiGetSearch(id) {
  const { data } = await api.get(`/searches/${id}`);
  return data;
}
export async function apiHistory() {
  const { data } = await api.get("/searches");
  return data;
}
export async function apiNameDetails(payload) {
  const { data } = await api.post("/name-details", payload);
  return data;
}
export async function apiCheckDomains(domains) {
  const { data } = await api.post("/check-domains", { domains });
  return data;
}

// ---------- saved ----------
export async function apiGetSaved() {
  const { data } = await api.get("/saved");
  return data;
}
export async function apiSaveName(payload) {
  const { data } = await api.post("/saved", payload);
  return data;
}
export async function apiDeleteSaved(id) {
  const { data } = await api.delete(`/saved/${id}`);
  return data;
}

// ---------- billing / promo ----------
export async function apiBillingSync(plan, platform = "web") {
  const { data } = await api.post("/billing/sync", { plan, platform });
  return data;
}
export async function apiRedeemPromo(code) {
  const { data } = await api.post("/promo/redeem", { code });
  return data;
}

// ---------- local backend: stripe + analytics ----------
export async function apiCreateCheckout({ plan, billing, user_id }) {
  const { data } = await local.post("/payments/checkout", {
    plan,
    billing,
    user_id,
    origin_url: window.location.origin,
  });
  return data;
}
export async function apiPaymentStatus(sessionId) {
  const { data } = await local.get(`/payments/status/${sessionId}`);
  return data;
}
export async function apiBillingPortal(user_id) {
  const { data } = await local.post("/payments/portal", { user_id, origin_url: window.location.origin });
  return data;
}
export function trackEvent(event, properties = {}, ctx = {}) {
  try {
    local
      .post("/analytics/track", {
        event,
        properties,
        anon_id: getGuestId() || localStorage.getItem(DEVICE_KEY),
        user_id: ctx.user_id || null,
        plan: ctx.plan || null,
        platform: "web",
      })
      .catch(() => {});
  } catch (e) {
    /* analytics must never break the product */
  }
}
